import type { IFieldElem } from '@models/IFormData';
import type { IDocumentScheme } from '@models/document/IDocumentScheme';
import { buildDependencyGraph } from '@utils/debug/FormulaDependencyParser';

export function getFieldDisplayName(scheme: IDocumentScheme | undefined, fieldKey: string | undefined): string {
    const pending: unknown[] = scheme ? [scheme.form] : [];
    while (pending.length > 0) {
        const node = pending.pop();
        if (!node || typeof node !== 'object') continue;
        if ('key' in node && node.key === fieldKey && 'name' in node && typeof node.name === 'string') return node.name;
        pending.push(...Object.values(node));
    }
    return fieldKey?.split('|').at(-1)?.replaceAll('_', ' ') || 'Поле';
}

export const RELATED_ROLE_NAMES: Record<string, string> = {
    required: 'Обязательность', visibility: 'Видимость', readonly: 'Редактирование',
    formula: 'Вычисление', min: 'Нижняя граница', max: 'Верхняя граница',
    validator: 'Валидация', 'validator-msg': 'Сообщение валидации',
    addRowButton: 'Добавление строки', editInForm: 'Редактирование в форме',
};

/**
 * ValueType контрола → базовый datatype, который понимает parseInspectorValue.
 * Нужен, чтобы редактор жучка парсил ввод так же, как прямая запись из FormInspector
 * (money/integer/boolean/longtext), а не гонял всё строкой.
 */
export function valueTypeToDatatype(valueType?: string): string | undefined {
    switch (valueType) {
        case 'money':
            return 'money';
        case 'double':
            return 'real';
        case 'integer':
        case 'year':
            return 'integer';
        case 'boolean':
        case 'yesno':
            return 'boolean';
        case 'longtext':
        case 'multiplestring':
            return 'longtext';
        case 'text':
            return 'text';
        default:
            return undefined;
    }
}

/** Роль связанного реквизита в формулах основного поля — метка чипа + заголовок. */
export const ROLE_META: Record<string, { label: string; title: string; variant: string }> = {
    required: { label: 'req', title: 'Питает формулу обязательности (required)', variant: 'req' },
    visibility: { label: 'vis', title: 'Питает формулу видимости (visibilityRules)', variant: 'vis' },
    readonly: { label: 'ro', title: 'Питает формулу «только чтение» (readonly)', variant: 'ro' },
    formula: { label: 'calc', title: 'Питает формулу вычисления (formula)', variant: 'max' },
    min: { label: 'min', title: 'Питает нижнюю границу (min)', variant: 'max' },
    max: { label: 'max', title: 'Питает верхнюю границу (max)', variant: 'max' },
    validator: { label: 'val', title: 'Питает условие валидатора документа', variant: 'ro' },
    'validator-msg': { label: 'val-msg', title: 'Питает сообщение валидатора документа', variant: 'ro' },
    addRowButton: { label: 'addRow', title: 'Питает видимость кнопки добавления строки', variant: 'vis' },
    editInForm: { label: 'editForm', title: 'Питает формулу редактирования в форме', variant: 'ro' },
};

export interface IRelatedRequisite {
    /** Полный ключ реквизита (pipe-путь `|Document|X`) — он же fieldName для прямой записи. */
    key: string;
    /** Отображаемое имя поля. */
    name: string;
    /** RHF-путь `fields.[N].value` для записи в форму; null, если поля нет в данных документа. */
    rhfPath: string | null;
    /** Текущее значение из данных формы. */
    value: IFieldElem['value'];
    /** Роли, в которых реквизит участвует в формулах основного поля (req/vis/ro/...). */
    roles: string[];
}

/**
 * Реквизиты, которые питают формулы данного поля (его `dependsOn` в графе зависимостей),
 * с текущими значениями и RHF-путями. Основной реквизит из списка исключён.
 */
export function getRelatedRequisites(
    scheme: IDocumentScheme | undefined,
    fieldKey: string | undefined,
    fieldsData: Record<string, IFieldElem> | undefined,
): IRelatedRequisite[] {
    if (!scheme || !fieldKey) return [];
    const nodes = buildDependencyGraph(scheme);
    const node = nodes.find((n) => n.fieldKey === fieldKey);
    if (!node || node.dependsOn.length === 0) return [];

    const nameByKey = new Map<string, string>();
    for (const n of nodes) nameByKey.set(n.fieldKey, n.fieldName);

    return node.dependsOn.map((edge) => {
        const elem = fieldsData?.[edge.key];
        return {
            key: edge.key,
            name: nameByKey.get(edge.key) || edge.key,
            rhfPath: elem?.name ?? null,
            value: elem?.value,
            roles: edge.types,
        };
    });
}
